/**
 * Staff permission groups — internal admin users only (not vendor / villager).
 * Each right is Yes/No on the role (merged when staff has multiple roles).
 */

export const STAFF_SCOPE_TYPES = [
  { value: 'all', label: 'All — entire organization' },
  { value: 'bu', label: 'Business Unit only' },
  { value: 'bu_site', label: 'Business Unit + Site' },
];

/** Checkbox groups shown on role preview / future roles screen. */
export const PERMISSION_GROUPS = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    rights: [{ key: 'view', label: 'Can view dashboard' }],
  },
  {
    id: 'users',
    label: 'Members',
    rights: [
      { key: 'view', label: 'Can view' },
      { key: 'kyc', label: 'KYC verify / reject / notify' },
      { key: 'documents', label: 'View / manage documents' },
    ],
  },
  {
    id: 'villagers',
    label: 'Villagers (legacy)',
    rights: [
      { key: 'view', label: 'Can view' },
      { key: 'edit', label: 'Can add / edit' },
      { key: 'delete', label: 'Can delete' },
      { key: 'kyc', label: 'Villager KYC' },
      { key: 'documents', label: 'Upload / manage documents' },
    ],
  },
  {
    id: 'vendors',
    label: 'Vendors (legacy)',
    rights: [
      { key: 'view', label: 'Can view' },
      { key: 'edit', label: 'Can add / edit' },
      { key: 'delete', label: 'Can delete' },
      { key: 'kyc', label: 'Vendor KYC' },
      { key: 'documents', label: 'Upload / manage documents' },
    ],
  },
  {
    id: 'equipment',
    label: 'Equipment fleet',
    rights: [
      { key: 'view', label: 'Can view' },
      { key: 'edit', label: 'Can add / edit' },
      { key: 'delete', label: 'Can delete' },
      { key: 'kyc', label: 'Equipment KYC' },
      { key: 'documents', label: 'Document verification' },
    ],
  },
  {
    id: 'indents',
    label: 'Indents / Requests',
    rights: [
      { key: 'view', label: 'Can view' },
      { key: 'edit', label: 'Can create / edit' },
      { key: 'delete', label: 'Can delete / cancel' },
      { key: 'recommend', label: 'Can recommend' },
      { key: 'deployment', label: 'Can update deployment status' },
      { key: 'release', label: 'Can release equipment' },
    ],
  },
  {
    id: 'master_villages',
    label: 'Master — Villages',
    rights: [
      { key: 'view', label: 'Can view' },
      { key: 'edit', label: 'Can add / edit' },
      { key: 'delete', label: 'Can delete' },
    ],
  },
  {
    id: 'master_bu',
    label: 'Master — BU & Site',
    rights: [
      { key: 'view', label: 'Can view' },
      { key: 'edit', label: 'Can add / edit' },
      { key: 'delete', label: 'Can delete' },
    ],
  },
  {
    id: 'master_equipment',
    label: 'Master — Equipment types',
    rights: [
      { key: 'view', label: 'Can view' },
      { key: 'edit', label: 'Can add / edit' },
      { key: 'delete', label: 'Can delete' },
    ],
  },
  {
    id: 'staff',
    label: 'Staff & roles',
    rights: [
      { key: 'view', label: 'Can view staff' },
      { key: 'edit', label: 'Can add / edit staff' },
      { key: 'delete', label: 'Can delete staff' },
      { key: 'roles', label: 'Can manage roles' },
      { key: 'management', label: 'Staff management screen access' },
      { key: 'activities', label: 'View activity logs' },
    ],
  },
  {
    id: 'system',
    label: 'System configuration',
    rights: [
      { key: 'view', label: 'Can view system config' },
      { key: 'edit', label: 'Can change system config' },
    ],
  },
];

export function emptyPermissions() {
  const perms = {};
  PERMISSION_GROUPS.forEach((g) => {
    perms[g.id] = {};
    g.rights.forEach((r) => {
      perms[g.id][r.key] = false;
    });
  });
  return perms;
}

/** Every right enabled — used by Super Admin. */
export function allPermissions() {
  const perms = {};
  PERMISSION_GROUPS.forEach((g) => {
    perms[g.id] = {};
    g.rights.forEach((r) => {
      perms[g.id][r.key] = true;
    });
  });
  return perms;
}

export function mergeRolePermissions(roleList) {
  const merged = emptyPermissions();
  roleList.forEach((role) => {
    const p = role?.permissions || {};
    PERMISSION_GROUPS.forEach((g) => {
      g.rights.forEach((r) => {
        if (p[g.id]?.[r.key]) merged[g.id][r.key] = true;
      });
    });
  });
  return merged;
}

/** Short labels for table / preview. */
export function permissionSummaryLines(permissions) {
  const lines = [];
  PERMISSION_GROUPS.forEach((g) => {
    const p = permissions[g.id] || {};
    const on = g.rights.filter((r) => p[r.key]).map((r) => r.label);
    if (on.length) lines.push(`${g.label}: ${on.join(', ')}`);
  });
  return lines;
}
